import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, Share2, Mic, Square, ArrowLeft, ArrowRight, Volume2, VolumeX, Loader2, Check, Lock, Play, CreditCard, Mail, Ticket,
} from 'lucide-react';
import { analytics } from '../utils/analytics';
import { playAck } from '../utils/animalSound';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveMedia = (u) => (!u ? '' : u.startsWith('/api') ? `${BACKEND_URL}${u}` : u);
const getSessionId = () => {
  let id = localStorage.getItem('vent_session_id');
  if (!id) {
    id = 'anon_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('vent_session_id', id);
  }
  return id;
};

const PACKAGES = [
  { id: 'single', vents: 1, price: '$1.00' },
  { id: 'pack5', vents: 5, price: '$2.50', badge: 'Popular' },
  { id: 'pack10', vents: 10, price: '$5.00', badge: 'Best value' },
];
const priceOf = (id) => PACKAGES.find((p) => p.id === id)?.price || '';

const VentToDogPage = () => {
  const navigate = useNavigate();
  const sessionId = getSessionId();

  const [step, setStep] = useState('intro'); // intro | select | vent | paywall | outro
  const [dogs, setDogs] = useState([]);
  const [loadingDogs, setLoadingDogs] = useState(true);
  const [selectedDog, setSelectedDog] = useState(null);
  const [sessionUuid, setSessionUuid] = useState(null);
  const [starting, setStarting] = useState(false);
  const [payProcessing, setPayProcessing] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('pack5');
  const [payEmail, setPayEmail] = useState(localStorage.getItem('vent_email') || '');
  const [credits, setCredits] = useState(0);

  // intro
  const [introMuted, setIntroMuted] = useState(true);
  const introVideoRef = useRef(null);

  // vent timer
  const [elapsed, setElapsed] = useState(0);
  const [ventBubble, setVentBubble] = useState(null);
  const presenceTimers = useRef([]);

  // outro
  const [hearted, setHearted] = useState(false);
  const [shared, setShared] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [ackLabel, setAckLabel] = useState('');
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [wantMore, setWantMore] = useState(false);
  const [woofUrl, setWoofUrl] = useState('');
  const [recording, setRecording] = useState(false);
  const [uploadingWoof, setUploadingWoof] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // ---- data ----
  const fetchDogs = useCallback(async () => {
    try {
      setLoadingDogs(true);
      const res = await axios.get(`${API}/dogs`);
      setDogs(res.data.dogs || []);
    } catch {
      toast.error('Could not load the pack. Try again.');
    } finally {
      setLoadingDogs(false);
    }
  }, []);

  useEffect(() => { fetchDogs(); }, [fetchDogs]);

  // Fire a GA impression per buddy when the select screen shows them (traction per dog/cat)
  const impressedRef = useRef(false);
  useEffect(() => {
    if (step === 'select' && !loadingDogs && dogs.length && !impressedRef.current) {
      impressedRef.current = true;
      dogs.forEach((d) => analytics.dogImpression(d.ga_tag || d.slug, d.species));
    }
  }, [step, loadingDogs, dogs]);

  // Returning buyer: show remaining prepaid vents on the select screen
  useEffect(() => {
    if (step !== 'select') return;
    const em = localStorage.getItem('vent_email');
    if (!em) return;
    axios.get(`${API}/dog-vent/credits?email=${encodeURIComponent(em)}`)
      .then((r) => setCredits(r.data.credits || 0))
      .catch(() => {});
  }, [step]);

  // Human-written "presence" bubbles that appear gently while venting
  useEffect(() => {
    if (step !== 'vent' || !selectedDog) return;
    let cancelled = false;
    axios.get(`${API}/dog-vent/presence/${encodeURIComponent(selectedDog.slug)}`).then((r) => {
      if (cancelled) return;
      const lines = (r.data.lines || []).slice();
      if (!lines.length) return;
      let i = 0;
      const show = () => {
        setVentBubble(lines[i % lines.length]);
        i += 1;
        presenceTimers.current.push(setTimeout(() => setVentBubble(null), 6000));
      };
      presenceTimers.current.push(setTimeout(show, 3500));
      presenceTimers.current.push(setInterval(show, 13000));
    }).catch(() => {});
    return () => {
      cancelled = true;
      presenceTimers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
      presenceTimers.current = [];
      setVentBubble(null);
    };
  }, [step, selectedDog]);

  // Handle return from Stripe Checkout (success/cancel) on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'cancelled') {
      toast.info('Payment cancelled.');
      window.history.replaceState({}, '', '/vent-to-dog');
      return;
    }
    if (payment === 'stripe') {
      const cs = params.get('cs');
      const dogSlug = params.get('dog');
      if (cs && dogSlug) handleStripeReturn(cs, dogSlug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStripeReturn = async (cs, dogSlug) => {
    setStep('verifying');
    try {
      const dogRes = await axios.get(`${API}/dogs/${dogSlug}`);
      setSelectedDog(dogRes.data);
    } catch { /* ignore */ }
    let paid = false;
    for (let i = 0; i < 15; i++) {
      try {
        const r = await axios.get(`${API}/payments/stripe/status/${cs}`);
        if (r.data.payment_status === 'paid') { setSessionUuid(r.data.dog_session_id); paid = true; break; }
        if (r.data.status === 'expired') break;
      } catch { /* keep polling */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
    window.history.replaceState({}, '', '/vent-to-dog');
    if (paid) { analytics.dogVentStart(dogSlug, false, selectedDog?.species); setStep('vent'); }
    else { toast.error('Payment not confirmed. Please try again.'); setStep('select'); }
  };

  const startStripe = async (packageId) => {
    if (!selectedDog) return;
    const pkg = packageId || selectedPackage;
    const em = payEmail.trim().toLowerCase();
    if (!em || !em.includes('@') || !em.includes('.')) {
      toast.error('Enter a valid email so we can save your vents.');
      return;
    }
    localStorage.setItem('vent_email', em);
    setPayProcessing('stripe');
    try {
      const r = await axios.post(`${API}/payments/stripe/checkout`, {
        dog_slug: selectedDog.slug, session_id: sessionId, origin_url: window.location.origin,
        package_id: pkg, email: em,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start card checkout.');
      setPayProcessing('');
    }
  };

  // intro auto-advance
  useEffect(() => {
    if (step !== 'intro') return;
    const t = setTimeout(() => setStep('select'), 4000);
    return () => clearTimeout(t);
  }, [step]);

  // vent timer
  useEffect(() => {
    if (step !== 'vent') return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [step]);

  const handleSelectDog = async (dog) => {
    setSelectedDog(dog);
    setStarting(true);
    try {
      const elig = await axios.get(`${API}/dog-vent/eligibility?session_id=${encodeURIComponent(sessionId)}`);
      if (elig.data.free_session_available) {
        const res = await axios.post(
          `${API}/dog-vent/sessions/start?session_id=${encodeURIComponent(sessionId)}&dog_slug=${encodeURIComponent(dog.slug)}`
        );
        setSessionUuid(res.data.id);
        analytics.dogVentStart(dog.ga_tag || dog.slug, true, dog.species);
        setStep('vent');
        return;
      }
      // Free vent used — try prepaid credits tied to the saved email
      const storedEmail = localStorage.getItem('vent_email') || '';
      if (storedEmail) {
        try {
          const cr = await axios.get(`${API}/dog-vent/credits?email=${encodeURIComponent(storedEmail)}`);
          if ((cr.data.credits || 0) > 0) {
            const sp = await axios.post(`${API}/dog-vent/sessions/start-paid`, {
              session_id: sessionId, dog_slug: dog.slug, email: storedEmail,
            });
            setSessionUuid(sp.data.session.id);
            setCredits(sp.data.credits_remaining);
            analytics.dogVentStart(dog.ga_tag || dog.slug, false, dog.species);
            setStep('vent');
            return;
          }
        } catch { /* fall through to paywall */ }
      }
      setStep('paywall');
    } catch (err) {
      if (err.response?.status === 402) {
        setStep('paywall');
      } else {
        toast.error(err.response?.data?.detail || 'Could not start the session.');
        setSelectedDog(null);
      }
    } finally {
      setStarting(false);
    }
  };

  const finishVent = () => {
    analytics.dogVentComplete(selectedDog?.ga_tag || selectedDog?.slug, selectedDog?.species);
    const isCat = (selectedDog?.species || '').toLowerCase() === 'cat';
    playAck(selectedDog?.species);
    setAckLabel(isCat ? 'Meow.' : 'Woof.');
    setStep('outro');
    setTimeout(() => setAckLabel(''), 2600);
  };

  const submitEmail = async (e, source = 'vent_outro') => {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribers`, { email: val, source, dog_slug: selectedDog?.slug || '' });
      setSubscribed(true);
      analytics.emailCapture(selectedDog?.ga_tag || selectedDog?.slug || source, selectedDog?.species);
      toast.success("You're on the list — first dibs on new adventures! 🐾");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your email. Try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const toggleHeart = () => {
    setHearted((h) => {
      const next = !h;
      if (next) analytics.dogHeart(selectedDog?.ga_tag || selectedDog?.slug, selectedDog?.species);
      return next;
    });
  };

  // ---- IG share card (canvas, dog's first frame as bg) ----
  const captureDogFrame = (videoUrl) => new Promise((resolve) => {
    if (!videoUrl) return resolve(null);
    const v = document.createElement('video');
    v.crossOrigin = 'anonymous';
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.src = videoUrl;
    const done = (val) => resolve(val);
    v.onloadeddata = () => {
      try { v.currentTime = Math.min(0.6, (v.duration || 1) / 2); } catch { done(null); }
    };
    v.onseeked = () => done(v);
    v.onerror = () => done(null);
    setTimeout(() => done(v.videoWidth ? v : null), 4000);
  });

  const generateShareCard = async () => {
    const W = 1080, H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0f0d';
    ctx.fillRect(0, 0, W, H);
    const frame = await captureDogFrame(resolveMedia(selectedDog?.video_url));
    if (frame && frame.videoWidth) {
      const vr = frame.videoWidth / frame.videoHeight;
      const cr = W / H;
      let dw, dh, dx, dy;
      if (vr > cr) { dh = H; dw = H * vr; dx = (W - dw) / 2; dy = 0; }
      else { dw = W; dh = W / vr; dx = 0; dy = (H - dh) / 2; }
      try { ctx.drawImage(frame, dx, dy, dw, dh); } catch { /* tainted */ }
    }
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, 'rgba(5,10,8,0.62)');
    g.addColorStop(0.42, 'rgba(5,10,8,0.12)');
    g.addColorStop(1, 'rgba(5,10,8,0.94)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'left';
    // top brand
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = '700 46px Poppins, Inter, sans-serif';
    ctx.fillText('VENT', 80, 130);
    ctx.fillStyle = 'rgba(110,231,183,0.92)';
    ctx.font = '400 30px Poppins, Inter, sans-serif';
    ctx.fillText('No judgement. Just a dog who listens.', 80, 180);
    // bottom message
    ctx.fillStyle = 'rgba(255,255,255,0.96)';
    ctx.font = '800 90px Poppins, Inter, sans-serif';
    ctx.fillText('I just vented to', 80, H - 370);
    ctx.fillStyle = '#6ee7b7';
    ctx.fillText(`${selectedDog?.name || 'a good dog'} 🐾`, 80, H - 260);
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    ctx.font = '400 44px Poppins, Inter, sans-serif';
    ctx.fillText('No judgement. Just calm.', 80, H - 170);
    // QR code -> /vent-to-dog (bottom-right, white panel)
    try {
      const qrUrl = await QRCode.toDataURL(`${window.location.origin}/vent-to-dog`, {
        margin: 1, width: 220, color: { dark: '#0a0f0d', light: '#ffffff' },
      });
      const qrImg = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = qrUrl;
      });
      const qs = 200, pad = 24, px = W - qs - pad - 60, py = H - qs - pad - 150;
      ctx.fillStyle = '#ffffff';
      const r = 28;
      ctx.beginPath();
      ctx.moveTo(px - pad + r, py - pad);
      ctx.arcTo(px + qs + pad, py - pad, px + qs + pad, py + qs + pad, r);
      ctx.arcTo(px + qs + pad, py + qs + pad, px - pad, py + qs + pad, r);
      ctx.arcTo(px - pad, py + qs + pad, px - pad, py - pad, r);
      ctx.arcTo(px - pad, py - pad, px + qs + pad, py - pad, r);
      ctx.closePath();
      ctx.fill();
      ctx.drawImage(qrImg, px, py, qs, qs);
      ctx.fillStyle = 'rgba(10,15,13,0.85)';
      ctx.font = '600 22px Poppins, Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Scan to vent', px + qs / 2, py + qs + 8);
      ctx.textAlign = 'left';
    } catch { /* QR optional — skip if generation fails */ }
    return new Promise((res) => canvas.toBlob((b) => res(b), 'image/png', 0.95));
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const caption = `I just vented to ${selectedDog?.name || 'a good dog'} 🐾 No judgement. Just calm. Try it → ${window.location.origin}`;
    try {
      try { await (document.fonts?.ready); } catch { /* ignore */ }
      const blob = await generateShareCard();
      const file = blob ? new File([blob], `vent-${selectedDog?.slug || 'dog'}.png`, { type: 'image/png' }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: 'Vent to a Dog' });
      } else if (file) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        try { await navigator.clipboard.writeText(caption); } catch { /* ignore */ }
        toast.success('Story card saved + caption copied — post it to your IG story!');
      } else if (navigator.share) {
        await navigator.share({ title: 'Vent to a Dog', text: caption, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(caption);
        toast.success('Copied — paste it into your IG story!');
      }
      setShared(true);
      analytics.dogShare(selectedDog?.ga_tag || selectedDog?.slug, selectedDog?.species);
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Could not build your share card. Try again.');
    } finally {
      setSharing(false);
    }
  };

  // ---- woof recording ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await uploadWoof(blob);
      };
      mr.start();
      setRecording(true);
      setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') stopRecording(); }, 3000);
    } catch {
      toast.error('Microphone access needed for your Woof reply.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const uploadWoof = async (blob) => {
    if (!sessionUuid) return;
    setUploadingWoof(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'woof.webm');
      const res = await axios.post(`${API}/dog-vent/sessions/${sessionUuid}/woof`, fd);
      setWoofUrl(resolveMedia(res.data.woof_url));
      analytics.dogWoof(selectedDog?.ga_tag || selectedDog?.slug, selectedDog?.species);
      toast.success('Woof saved! 🐶');
    } catch {
      toast.error('Could not save your Woof.');
    } finally {
      setUploadingWoof(false);
    }
  };

  const finalizeAndExit = async () => {
    if (sessionUuid) {
      try {
        await axios.post(`${API}/dog-vent/sessions/${sessionUuid}/finalize?hearted=${hearted}&shared=${shared}`);
      } catch { /* best-effort */ }
    }
    navigate('/');
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ========================= RENDER =========================

  // INTRO — Dog Sage "WOOF WOOF"
  if (step === 'intro') {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center overflow-hidden" data-testid="dog-intro">
        <video
          ref={introVideoRef}
          src="/dog-sage.mp4"
          autoPlay
          loop
          muted={introMuted}
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-black/40" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative z-10 text-center px-6">
          <motion.h1
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="font-heading font-extrabold text-5xl sm:text-7xl text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.7)]"
          >
            WOOF WOOF
          </motion.h1>
          <p className="mt-4 text-white/80 text-lg">No judgement. No advice. Just a dog who listens.</p>
        </motion.div>
        <button onClick={() => setIntroMuted((m) => !m)} className="absolute top-5 right-5 z-20 p-3 glass rounded-full" data-testid="intro-mute-btn">
          {introMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-emerald-400" />}
        </button>
        <button onClick={() => setStep('select')} className="absolute bottom-6 right-6 z-20 text-white/70 hover:text-white text-sm tracking-wider" data-testid="intro-skip-btn">
          SKIP →
        </button>
      </div>
    );
  }

  // SELECT — choose a dog/cat
  if (step === 'select') {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern px-4 sm:px-6 py-8" data-testid="dog-select">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> Back to the Almanac
          </button>
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-3">Adventure 01 · Vent to a Dog</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-2">Choose your listener</h1>
            <p className="text-zinc-400">Your first vent is on us 🐾 Pick a friend and let it all out.</p>
            <p className="text-xs text-zinc-600 mt-2">An early experiment — after you vent, tell us how it felt.</p>
            {credits > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400/10 border border-emerald-400/30" data-testid="credits-badge">
                <Ticket className="w-4 h-4 text-emerald-300" />
                <span className="text-sm text-emerald-200">Welcome back — you have <span className="font-bold">{credits}</span> {credits === 1 ? 'vent' : 'vents'} left</span>
              </div>
            )}
          </div>

          {loadingDogs ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
          ) : dogs.length === 0 ? (
            <p className="text-center text-zinc-500 py-20">No friends available yet. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
              {dogs.map((dog, i) => (
                <motion.button
                  key={dog.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -5 }}
                  onClick={() => handleSelectDog(dog)}
                  disabled={starting}
                  data-testid={`dog-card-${dog.slug}`}
                  className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/50 text-left disabled:opacity-60"
                >
                  <div className="relative w-full aspect-[3/4] overflow-hidden">
                    {dog.thumbnail_url ? (
                      <img src={resolveMedia(dog.thumbnail_url)} alt={dog.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    ) : (
                      <video
                        src={`${resolveMedia(dog.video_url)}#t=0.1`}
                        muted
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}
                    {starting && selectedDog?.slug === dog.slug && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-300/80">{dog.species}</p>
                    <h3 className="font-heading font-bold text-xl text-white mt-0.5">{dog.name}</h3>
                    <p className="text-xs text-zinc-400 mt-1">{dog.vibe}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          )}

          {/* Curiosity hook — gauge demand for more buddies + capture emails */}
          {!loadingDogs && dogs.length > 0 && (
            <div className="mt-10 text-center" data-testid="want-more-block">
              {subscribed ? (
                <p className="text-sm text-emerald-300" data-testid="want-more-subscribed">You're in — we'll bring the whole pack to you first. 🐾</p>
              ) : !wantMore ? (
                <button
                  onClick={() => { setWantMore(true); analytics.wantMore(); }}
                  data-testid="want-more-btn"
                  className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-emerald-300 transition-colors"
                >
                  <span>Wanna see more dogs &amp; cats?</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm mx-auto">
                  <p className="text-sm text-white/90 mb-1">More friends are on the way 🐾</p>
                  <p className="text-xs text-zinc-500 mb-3">Drop your email and you'll be first to meet the full pack.</p>
                  <form onSubmit={(e) => submitEmail(e, 'want_more')} className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        data-testid="want-more-email-input"
                        className="w-full bg-black/30 border border-white/10 rounded-full pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={subscribing || !email.trim()}
                      data-testid="want-more-submit"
                      className="px-4 py-2.5 rounded-full bg-emerald-400 text-[#08130d] font-heading font-bold text-sm hover:scale-[1.03] transition-transform disabled:opacity-50 disabled:hover:scale-100 whitespace-nowrap"
                    >
                      {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Notify me'}
                    </button>
                  </form>
                </motion.div>
              )}
            </div>
          )}

          {/* Community — invite people to submit their own dog/cat */}
          {!loadingDogs && dogs.length > 0 && (
            <div className="mt-14 max-w-2xl mx-auto text-center border-t border-white/10 pt-10" data-testid="community-submit-block">
              <p className="text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-3">Join the pack</p>
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white mb-3">Want your dog or cat in here?</h3>
              <p className="text-sm text-zinc-400 mb-1">
                Send us a <span className="text-white">1-minute video</span> of just your dog or cat looking straight into the camera — <span className="text-white">no humans</span> in frame.
              </p>
              <p className="text-sm text-zinc-400 mb-5">
                Include their <span className="text-white">name</span> and a few lines about their <span className="text-white">personality</span>. We'll have a look and add the good ones. 🐾
              </p>
              <a
                href="mailto:ventbackatus@gmail.com?subject=My%20dog%2Fcat%20for%20Vent&body=Name%3A%20%0ASpecies%20(dog%2Fcat)%3A%20%0APersonality%20(a%20few%20lines)%3A%20%0A%0A(Attach%20your%201-minute%20video%20-%20just%20the%20dog%2Fcat%2C%20looking%20into%20the%20camera%2C%20no%20humans)"
                data-testid="community-submit-email"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400 text-[#08130d] font-heading font-bold text-sm hover:scale-[1.03] transition-transform"
              >
                <Mail className="w-4 h-4" /> Send to ventbackatus@gmail.com
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // VERIFYING — returning from Stripe, confirming payment
  if (step === 'verifying') {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern flex flex-col items-center justify-center px-5" data-testid="dog-verifying">
        <Loader2 className="w-9 h-9 text-emerald-400 animate-spin mb-4" />
        <p className="text-white font-heading">Confirming your payment…</p>
        <p className="text-zinc-500 text-sm mt-1">Hang tight, almost there.</p>
      </div>
    );
  }

  // PAYWALL — free session used → choose a payment method
  if (step === 'paywall') {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern flex items-center justify-center px-5 py-10" data-testid="dog-paywall">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 max-w-md w-full text-center border border-white/10">
          <div className="w-14 h-14 rounded-2xl bg-emerald-400/15 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-emerald-400" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-white mb-2">Keep venting with {selectedDog?.name || 'your friend'}?</h2>
          <p className="text-zinc-400 mb-6">Your free vent is used. Grab a pack — bank a few for whenever life piles up.</p>

          <div className="space-y-2.5 text-left" data-testid="package-options">
            {PACKAGES.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedPackage(p.id)}
                data-testid={`package-${p.id}`}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-colors ${
                  selectedPackage === p.id ? 'border-emerald-400 bg-emerald-400/10' : 'border-white/10 bg-black/20 hover:border-white/25'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span className={`w-4 h-4 rounded-full border-2 ${selectedPackage === p.id ? 'border-emerald-400 bg-emerald-400' : 'border-white/30'}`} />
                  <span className="text-white font-semibold">{p.vents} {p.vents === 1 ? 'vent' : 'vents'}</span>
                  {p.badge && <span className="text-[9px] uppercase tracking-wider text-emerald-300 border border-emerald-300/40 rounded-full px-2 py-0.5">{p.badge}</span>}
                </span>
                <span className="text-white font-heading font-bold">{p.price}</span>
              </button>
            ))}
          </div>

          <div className="relative mt-4">
            <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={payEmail}
              onChange={(e) => setPayEmail(e.target.value)}
              placeholder="you@email.com — saves your vents"
              data-testid="paywall-email-input"
              className="w-full bg-black/30 border border-white/10 rounded-full pl-9 pr-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none"
            />
          </div>

          <button
            onClick={() => startStripe(selectedPackage)}
            disabled={!!payProcessing}
            data-testid="pay-stripe-btn"
            className="w-full mt-4 py-3.5 rounded-2xl bg-white text-black font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60"
          >
            {payProcessing === 'stripe' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
            Pay with Card · {priceOf(selectedPackage)}
          </button>

          <p className="text-[11px] text-zinc-600 mt-4">Secure card checkout via Stripe. Your vents are saved to your email, so you can use them on any device.</p>

          <button onClick={() => navigate('/')} className="mt-5 text-sm text-zinc-500 hover:text-zinc-300" data-testid="paywall-home-btn">
            Maybe later — back to the Almanac
          </button>
        </motion.div>
      </div>
    );
  }

  // VENT — looping video + breathing prompt
  if (step === 'vent') {
    return (
      <div className="fixed inset-0 bg-black z-50 overflow-hidden" data-testid="dog-vent-room">
        <video
          src={resolveMedia(selectedDog?.video_url)}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="dog-vent-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />

        <div className="relative z-10 h-full flex flex-col items-center justify-between py-10 px-6 text-center">
          <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-white/90 text-lg font-body">
            {selectedDog?.name} is listening. Let it out.
          </motion.p>

          <div className="flex-1 flex flex-col items-center justify-center gap-6">
            <div className="h-24 flex items-end justify-center">
              <AnimatePresence mode="wait">
                {ventBubble && (
                  <motion.div
                    key={ventBubble}
                    initial={{ opacity: 0, y: 12, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    data-testid="vent-presence-bubble"
                    className="relative max-w-xs px-5 py-3 rounded-3xl bg-white text-zinc-900 font-body text-lg shadow-xl"
                  >
                    {ventBubble}
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.div
              animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.9, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-28 h-28 rounded-full border-2 border-emerald-300/50 flex items-center justify-center"
            >
              <Mic className="w-9 h-9 text-emerald-200/80" />
            </motion.div>
            <p className="text-[11px] text-white/40">Real words, written by humans — never AI 🐾</p>
          </div>

          <div className="w-full max-w-sm">
            <p className="text-white/60 text-sm mb-4 tabular-nums" data-testid="vent-timer">{fmt(elapsed)}</p>
            <button
              onClick={finishVent}
              className="w-full py-4 rounded-full bg-white text-black font-heading font-bold tracking-wide hover:scale-[1.02] transition-transform"
              data-testid="vent-done-btn"
            >
              I'm done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // OUTRO — heart / share / woof
  const isCat = (selectedDog?.species || '').toLowerCase() === 'cat';
  return (
    <div className="min-h-screen bg-vent-bg bg-pattern flex items-center justify-center px-5 py-10" data-testid="dog-outro">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <div className="relative w-24 h-24 mx-auto mb-5">
          <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }} className="w-24 h-24 rounded-full overflow-hidden border-2 border-emerald-300/40 bg-emerald-950/40">
            {selectedDog?.thumbnail_url ? (
              <img src={resolveMedia(selectedDog?.thumbnail_url)} alt={selectedDog?.name} className="w-full h-full object-cover" />
            ) : (
              <video src={`${resolveMedia(selectedDog?.video_url)}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
            )}
          </motion.div>
          <AnimatePresence>
            {ackLabel && (
              <motion.div
                initial={{ opacity: 0, scale: 0.7, x: 8, y: 6 }}
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                className="absolute -top-2 -right-6 px-3 py-1.5 rounded-2xl rounded-bl-sm bg-white text-[#08130d] font-heading font-bold text-sm shadow-lg"
                data-testid="dog-ack-bubble"
              >
                {ackLabel}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <h2 className="font-heading font-bold text-3xl text-white mb-2">Feel lighter?</h2>
        <p className="text-zinc-400 mb-8">{selectedDog?.name} won't tell a soul.</p>

        <div className="flex items-center justify-center gap-4 mb-8">
          <button onClick={toggleHeart} data-testid="outro-heart-btn" className={`flex flex-col items-center gap-2 px-5 py-4 rounded-2xl glass border border-white/10 transition-colors ${hearted ? 'bg-rose-500/15' : ''}`}>
            <Heart className={`w-6 h-6 ${hearted ? 'text-rose-400 fill-rose-400' : 'text-zinc-300'}`} />
            <span className="text-xs text-zinc-400">{hearted ? 'Loved' : 'Like'}</span>
          </button>
          <button onClick={handleShare} disabled={sharing} data-testid="outro-share-btn" className={`flex flex-col items-center gap-2 px-5 py-4 rounded-2xl glass border border-white/10 transition-colors disabled:opacity-60 ${shared ? 'bg-emerald-500/15' : ''}`}>
            {sharing ? <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /> : shared ? <Check className="w-6 h-6 text-emerald-400" /> : <Share2 className="w-6 h-6 text-zinc-300" />}
            <span className="text-xs text-zinc-400">{sharing ? 'Making…' : shared ? 'Shared' : 'Share'}</span>
          </button>
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={uploadingWoof}
            data-testid="outro-woof-btn"
            className={`flex flex-col items-center gap-2 px-5 py-4 rounded-2xl glass border border-white/10 transition-colors ${recording ? 'bg-rose-500/20 animate-pulse' : ''}`}
          >
            {uploadingWoof ? <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /> : recording ? <Square className="w-6 h-6 text-rose-400" /> : <Mic className="w-6 h-6 text-zinc-300" />}
            <span className="text-xs text-zinc-400">{recording ? 'Stop' : woofUrl ? (isCat ? 'Re-meow' : 'Re-woof') : (isCat ? 'Meow back' : 'Woof back')}</span>
          </button>
        </div>

        {woofUrl && (
          <div className="mb-6 flex items-center justify-center gap-2" data-testid="woof-playback">
            <Play className="w-4 h-4 text-emerald-400" />
            <audio src={woofUrl} controls className="h-9" />
          </div>
        )}

        <button onClick={finalizeAndExit} className="w-full py-4 rounded-full bg-white text-black font-heading font-bold tracking-wide hover:scale-[1.02] transition-transform" data-testid="outro-done-btn">
          Done
        </button>
        <button onClick={() => setStep('select')} className="mt-4 text-sm text-zinc-500 hover:text-zinc-300" data-testid="outro-another-btn">
          Vent to another friend
        </button>
      </motion.div>
    </div>
  );
};

export default VentToDogPage;
